import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  Alert,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import { 
  Add as AddIcon, 
  Edit as EditIcon, 
  Delete as DeleteIcon, 
  Visibility as VisibilityIcon,
  Search as SearchIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { DataGrid } from '@mui/x-data-grid';
import axios from 'axios';
import { format } from 'date-fns';
import SecurityIcon from '@mui/icons-material/Security';
import { formatCurrency, DEFAULT_CURRENCY } from '../../utils/currency';

const StatCard = ({ title, value, icon, color, subtitle }) => (
  <Card sx={{ 
    height: '100%',
    background: 'linear-gradient(135deg,rgb(30, 30, 30) 0%,rgb(35, 34, 38) 50%)',
    border: 'none',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'linear-gradient(45deg, rgba(23, 23, 23, 0.05) 0%, rgba(255,255,255,0.02) 50%, rgba(255,255,255,0.05) 100%)',
      pointerEvents: 'none',
    },
  }}>
    <CardContent sx={{ position: 'relative', zIndex: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ color: '#ffffff', fontWeight: 700, mb: 1 }}>
            {value}
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 1 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
          }}
        >
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const columns = (handleView, handleEdit, handleDelete, handleStatusChange, editingStatus, setEditingStatus, handleStatusSave, handleStatusCancel, userSettings = {}) => [
  // Fraudster column removed
  {
    field: 'scamType',
    headerName: 'Scam Type',
    width: 180,
    renderCell: (params) => (
      <Typography 
        sx={{ 
          color: '#e0e0e0', 
          fontSize: '0.875rem',
          fontWeight: 400
        }}
      >
        {params.row.scamType}
      </Typography>
    ),
  },
  {
    field: 'amount',
    headerName: 'Amount',
    width: 120,
    renderCell: (params) => (
      <Typography 
        sx={{ 
          color: '#ffffff', 
          fontSize: '0.875rem',
          fontWeight: 600
        }}
      >
        {formatCurrency(params.row.amount || 0, userSettings.currency?.display || DEFAULT_CURRENCY)}
      </Typography>
    ),
  },
  {
    field: 'description',
    headerName: 'Description',
    width: 250,
    renderCell: (params) => (
      <Typography 
        sx={{ 
          color: '#bdbdbd', 
          fontSize: '0.875rem',
          fontWeight: 400,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}
        title={params.row.description}
      >
        {params.row.description || 'No description'}
      </Typography>
    ),
  },

  {
    field: 'status',
    headerName: 'Status',
    width: 140,
    renderCell: (params) => {
      const isEditing = editingStatus.id === params.id;
      
      if (isEditing) {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <Select
                value={editingStatus.status}
                onChange={(e) => setEditingStatus(prev => ({ ...prev, status: e.target.value }))}
                sx={{ 
                  color: '#ffffff',
                  bgcolor: '#2a2a2a',
                  '& .MuiSelect-icon': { color: '#ffffff' }
                }}
              >
                <MenuItem value="new">New</MenuItem>
                <MenuItem value="in-progress">In Progress</MenuItem>
                <MenuItem value="done">Done</MenuItem>
              </Select>
            </FormControl>
            <IconButton 
              size="small" 
              onClick={() => handleStatusSave(params.id)}
              sx={{ color: '#4caf50' }}
            >
              <SaveIcon sx={{ fontSize: 16 }} />
            </IconButton>
            <IconButton 
              size="small" 
              onClick={() => handleStatusCancel()}
              sx={{ color: '#f44336' }}
            >
              <CancelIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        );
      }
      
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Chip
            label={params.value.replace('-', ' ')}
          sx={{
              bgcolor: '#2a2a2a',
            color: '#ffffff',
              border: '1px solid #444',
              fontWeight: 500,
              fontSize: '0.75rem',
              height: 24,
              cursor: 'pointer',
              '&:hover': {
                bgcolor: '#333333',
              }
          }}
          size="small"
            onClick={() => setEditingStatus({ id: params.id, status: params.value })}
        />
        </Box>
      );
    },
  },
  // Priority column removed
  {
    field: 'createdAt',
    headerName: 'Created',
    width: 140,
    renderCell: (params) => (
      <Typography 
        sx={{ 
          color: '#bdbdbd', 
          fontSize: '0.875rem',
          fontWeight: 400
        }}
      >
        {format(new Date(params.value), 'MMM dd, yyyy')}
      </Typography>
    ),
  },
  {
    field: 'actions',
    headerName: 'Actions',
    width: 160,
    sortable: false,
    renderCell: (params) => (
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        <Tooltip title="View Details" arrow>
          <IconButton 
            onClick={() => handleView(params.id)} 
            sx={{ 
              color: '#757575',
              '&:hover': {
                bgcolor: '#75757515',
                color: '#9e9e9e'
              }
            }}
            size="small"
          >
            <VisibilityIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Edit Record" arrow>
          <IconButton 
            onClick={() => handleEdit(params.id)} 
            sx={{ 
              color: '#757575',
              '&:hover': {
                bgcolor: '#75757515',
                color: '#9e9e9e'
              }
            }}
            size="small"
          >
            <EditIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete Record" arrow>
          <IconButton 
            onClick={() => handleDelete(params.id)} 
            sx={{ 
              color: '#757575',
              '&:hover': {
                bgcolor: '#75757515',
                color: '#9e9e9e'
              }
            }}
            size="small"
          >
            <DeleteIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>
    ),
  },
];

const FraudRecords = () => {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingStatus, setEditingStatus] = useState({ id: null, status: '' });
  const [userSettings, setUserSettings] = useState({
    currency: { display: DEFAULT_CURRENCY }
  });
  const [stats, setStats] = useState({
    overview: {
      totalRecords: 0,
      totalAmount: 0,
      thisWeekRecords: 0,
      thisMonthRecords: 0,
      thisYearRecords: 0
    },
    distribution: {
      status: { new: 0, 'in-progress': 0, done: 0 },
      priority: { low: 0, medium: 0, high: 0, critical: 0 }
    }
  });

  useEffect(() => {
    fetchRecords();
    fetchUserSettings();
    fetchStats();
  }, []);

  useEffect(() => {
    filterRecords();
  }, [records, searchTerm]);

    const fetchRecords = async () => {
      try {
        const response = await axios.get('/api/fraud-records', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        const formattedRecords = response.data.records.map((record) => ({
          id: record._id,
          scamType: record.callDetails.scamType,
          status: record.status,
          createdAt: record.createdAt,
          description: record.callDetails.description,
          amount: record.callDetails.totalAmount,
        }));
        setRecords(formattedRecords);
      } catch (error) {
        console.error('Error fetching records:', error);
      } finally {
        setLoading(false);
      }
    };

  const fetchUserSettings = async () => {
    try {
      const response = await axios.get('/api/users/settings', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setUserSettings(response.data);
    } catch (error) {
      console.error('Error fetching user settings:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get('/api/fraud-records/stats', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const filterRecords = () => {
    if (!searchTerm.trim()) {
      setFilteredRecords(records);
      return;
    }

    const filtered = records.filter(record => 
      Object.values(record).some(value => 
        value && value.toString().toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
    setFilteredRecords(filtered);
  };

  const handleView = (id) => {
    navigate(`/fraud-records/${id}`);
  };

  const handleEdit = (id) => {
    navigate(`/fraud-records/${id}`);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this record?')) {
      try {
        await axios.delete(`/api/fraud-records/${id}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        setRecords(records.filter((record) => record.id !== id));
      } catch (error) {
        console.error('Error deleting record:', error);
      }
    }
  };

  const handleStatusChange = (id, newStatus) => {
    setEditingStatus({ id, status: newStatus });
  };

  const handleStatusSave = async (id) => {
    try {
      await axios.put(`/api/fraud-records/${id}`, 
        { status: editingStatus.status },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      setRecords(prev => prev.map(record => 
        record.id === id ? { ...record, status: editingStatus.status } : record
      ));
      
      setEditingStatus({ id: null, status: '' });
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status');
    }
  };

  const handleStatusCancel = () => {
    setEditingStatus({ id: null, status: '' });
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* Header Section */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 2,
        mb: 3
      }}>
        <Box>
          <Typography 
            variant="h4" 
            sx={{ 
              color: '#ffffff', 
              fontWeight: 700, 
              mb: 0.5,
              fontSize: { xs: '1.2rem', md: '1.2rem' }
            }}
          >
            Fraud Records
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              color: '#bdbdbd',
              fontSize: '0.875rem'
            }}
          >
            Manage and track all fraud cases in your system
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/fraud-records/new')}
          sx={{
            background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 25%, #0f3460 50%, #533483 75%, #6464ff 100%)',
            backgroundSize: '200% 200%',
            animation: 'gradientShift 3s ease infinite',
            color: '#ffffff',
            fontWeight: 600,
            fontSize: '1rem',
            px: 4,
            py: 1.5,
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(26, 26, 46, 0.4)',
            textTransform: 'none',
            '&:hover': {
              background: 'linear-gradient(135deg, #6464ff 0%, #533483 25%, #0f3460 50%, #16213e 75%, #1a1a2e 100%)',
              backgroundSize: '200% 200%',
              animation: 'gradientShift 2s ease infinite',
              boxShadow: '0 12px 40px rgba(26, 26, 46, 0.6)',
              transform: 'translateY(-2px)'
            },
            '&:active': {
              transform: 'translateY(0px)',
              boxShadow: '0 4px 20px rgba(26, 26, 46, 0.5)'
            },
            '@keyframes gradientShift': {
              '0%': {
                backgroundPosition: '0% 50%'
              },
              '50%': {
                backgroundPosition: '100% 50%'
              },
              '100%': {
                backgroundPosition: '0% 50%'
              }
            }
          }}
        >
          New Record
        </Button>
      </Box>

      {/* Stats Widgets */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Records"
            value={stats.overview.totalRecords}
            icon={<SecurityIcon sx={{ color: '#ffffff' }} />}
            color="default"
            subtitle="All fraud records"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Amount"
            value={formatCurrency((stats.overview.totalAmount / 1000), userSettings.currency.display) + 'k'}
            icon={<TrendingUpIcon sx={{ color: '#ffffff' }} />}
            color="success"
            subtitle="Scam amounts tracked"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="New Cases"
            value={stats.distribution.status.new || 0}
            icon={<SecurityIcon sx={{ color: '#ffffff' }} />}
            color="default"
            subtitle="Pending cases"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Completed"
            value={stats.distribution.status.done || 0}
            icon={<SecurityIcon sx={{ color: '#ffffff' }} />}
            color="success"
            subtitle="Resolved cases"
          />
        </Grid>
      </Grid>

      {/* Search Section */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
                          placeholder="Search by scam type, description, amount, or any other field..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#757575' }} />
              </InputAdornment>
            ),
            sx: { 
              color: '#ffffff',
              bgcolor: '#2a2a2a',
            }
          }}
          InputLabelProps={{ sx: { color: '#bdbdbd' } }}
          sx={{
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: '#444',
              },
              '&:hover fieldset': {
                borderColor: '#666',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#1976d2',
              },
            },
          }}
        />
        {searchTerm && (
          <Typography variant="body2" sx={{ color: '#757575', mt: 1 }}>
            Found {filteredRecords.length} record{filteredRecords.length !== 1 ? 's' : ''}
          </Typography>
        )}
      </Box>

      {/* Table Section */}
      <Box sx={{
        height: 600,
        width: '100%',
        '& .MuiDataGrid-root': {
          border: 'none',
          bgcolor: 'transparent',
          borderRadius: '0 !important',
        },
        '& .MuiDataGrid-cell': {
          borderBottom: 'none',
          bgcolor: 'transparent',
          borderRadius: '0 !important',
          '&:focus': {
            outline: 'none',
          },
          '&:focus-within': {
            outline: 'none',
          }
        },
        '& .MuiDataGrid-row': {
          borderRadius: '0 !important',
          bgcolor: 'transparent',
          '&:hover': {
            bgcolor: 'rgba(255,255,255,0.03)',
          },
          '&.Mui-selected': {
            bgcolor: 'rgba(25, 118, 210, 0.07)',
            '&:hover': {
              bgcolor: 'rgba(25, 118, 210, 0.13)',
            }
          }
        },
        '& .MuiDataGrid-columnHeaders': {
          bgcolor: 'transparent',
          borderBottom: 'none',
          color: '#ffffff',
          fontWeight: 600,
          fontSize: '0.875rem',
          borderRadius: '0 !important',
        },
        '& .MuiDataGrid-columnHeader': {
          borderRadius: '0 !important',
          bgcolor: 'transparent',
          borderRight: 'none',
          '&:focus': {
            outline: 'none',
          },
          '&:focus-within': {
            outline: 'none',
          }
        },
        '& .MuiDataGrid-footerContainer': {
          borderTop: 'none',
          color: '#bdbdbd',
          bgcolor: 'transparent',
          borderRadius: '0 !important',
        },
        '& .MuiDataGrid-toolbarContainer': {
          bgcolor: 'transparent',
          borderBottom: 'none',
          borderRadius: '0 !important',
          '& .MuiButton-text': {
          color: '#ffffff',
            borderRadius: '0 !important',
          },
        },
        '& .MuiDataGrid-virtualScroller': {
          bgcolor: 'transparent',
          borderRadius: '0 !important',
        },
        '& .MuiDataGrid-virtualScrollerContent': {
          bgcolor: 'transparent',
          borderRadius: '0 !important',
        },
        '& .MuiDataGrid-virtualScrollerRenderZone': {
          bgcolor: 'transparent',
          borderRadius: '0 !important',
        },
        '& .MuiDataGrid-panel': {
          borderRadius: '0 !important',
          bgcolor: 'rgba(30, 30, 30, 0.95)',
        },
        '& .MuiDataGrid-panelContent': {
          borderRadius: '0 !important',
          bgcolor: 'transparent',
        },
        '& .MuiDataGrid-panelFooter': {
          borderRadius: '0 !important',
          bgcolor: 'transparent',
        },
        '& .MuiDataGrid-columnMenu': {
          borderRadius: '0 !important',
          bgcolor: 'rgba(30, 30, 30, 0.95)',
        },
        '& .MuiDataGrid-columnMenuList': {
          borderRadius: '0 !important',
          bgcolor: 'transparent',
        },
        '& .MuiDataGrid-columnMenuPaper': {
          borderRadius: '0 !important',
          bgcolor: 'rgba(30, 30, 30, 0.95)',
        },
        '& .MuiDataGrid-filterForm': {
          borderRadius: '0 !important',
          bgcolor: 'transparent',
        },
        '& .MuiDataGrid-filterFormOperatorInput': {
          borderRadius: '0 !important',
          bgcolor: 'transparent',
        },
        '& .MuiDataGrid-filterFormColumnInput': {
          borderRadius: '0 !important',
          bgcolor: 'transparent',
        },
        '& .MuiDataGrid-filterFormValueInput': {
          borderRadius: '0 !important',
          bgcolor: 'transparent',
        },
        '& .MuiDataGrid-filterFormDeleteIcon': {
          borderRadius: '0 !important',
          bgcolor: 'transparent',
        },
        '& .MuiDataGrid-toolbarFilterList': {
          borderRadius: '0 !important',
          bgcolor: 'transparent',
        },
        '& .MuiDataGrid-footerPagination': {
          borderRadius: '0 !important',
          bgcolor: 'transparent',
        },
        '& .MuiDataGrid-footerPaginationInfo': {
          borderRadius: '0 !important',
          bgcolor: 'transparent',
        },
        '& .MuiDataGrid-footerPaginationSelect': {
          borderRadius: '0 !important',
          bgcolor: 'transparent',
        },
        '& .MuiDataGrid-footerPaginationSelectLabel': {
          borderRadius: '0 !important',
          bgcolor: 'transparent',
        },
        '& .MuiDataGrid-footerPaginationSelectIcon': {
          borderRadius: '0 !important',
          bgcolor: 'transparent',
        },
        '& .MuiDataGrid-footerPaginationSelectSelect': {
          borderRadius: '0 !important',
          bgcolor: 'transparent',
        },
        '& .MuiDataGrid-footerPaginationSelectOutlined': {
          borderRadius: '0 !important',
          bgcolor: 'transparent',
        },
        '& .MuiDataGrid-footerPaginationSelectFilled': {
          borderRadius: '0 !important',
          bgcolor: 'transparent',
        },
        '& .MuiDataGrid-footerPaginationSelectStandard': {
          borderRadius: '0 !important',
          bgcolor: 'transparent',
        },
        '& .MuiDataGrid-footerPaginationSelectRoot': {
          borderRadius: '0 !important',
          bgcolor: 'transparent',
        },
        '& .MuiDataGrid-footerPaginationSelectInput': {
          borderRadius: '0 !important',
          bgcolor: 'transparent',
        },
        '& .MuiDataGrid-footerPaginationSelectInputBase': {
          borderRadius: '0 !important',
          bgcolor: 'transparent',
        },
        '& .MuiDataGrid-footerPaginationSelectInputRoot': {
          borderRadius: '0 !important',
          bgcolor: 'transparent',
        },
        '& .MuiDataGrid-footerPaginationSelectInputInput': {
          borderRadius: '0 !important',
          bgcolor: 'transparent',
        },
        '& .MuiDataGrid-footerPaginationSelectInputInputAdornedStart': {
          borderRadius: '0 !important',
          bgcolor: 'transparent',
        },
        '& .MuiDataGrid-footerPaginationSelectInputInputAdornedEnd': {
          borderRadius: '0 !important',
          bgcolor: 'transparent',
        },
        '& .MuiDataGrid-footerPaginationSelectInputInputMultiline': {
          borderRadius: '0 !important',
          bgcolor: 'transparent',
        },
        '& .MuiDataGrid-footerPaginationSelectInputInputHiddenLabel': {
          borderRadius: '0 !important',
          bgcolor: 'transparent',
        },
        '& .MuiDataGrid-footerPaginationSelectInputInputSizeSmall': {
          borderRadius: '0 !important',
          bgcolor: 'transparent',
        },
        '& .MuiDataGrid-footerPaginationSelectInputInputSizeLarge': {
          borderRadius: '0 !important',
          bgcolor: 'transparent',
        },
        '& .MuiDataGrid-footerPaginationSelectInputInputFullWidth': {
          borderRadius: '0 !important',
          bgcolor: 'transparent',
        },
        '& .MuiDataGrid-footerPaginationSelectInputInputDisableUnderline': {
          borderRadius: '0 !important',
          bgcolor: 'transparent',
        },
        '& .MuiDataGrid-footerPaginationSelectInputInputNotchedOutline': {
          borderRadius: '0 !important',
          bgcolor: 'transparent',
        },
        '& .MuiDataGrid-footerPaginationSelectInputInputOutlinedInput': {
          borderRadius: '0 !important',
          bgcolor: 'transparent',
        },
        '& .MuiDataGrid-footerPaginationSelectInputInputFilledInput': {
          borderRadius: '0 !important',
          bgcolor: 'transparent',
        },
        '& .MuiDataGrid-footerPaginationSelectInputInputStandardInput': {
          borderRadius: '0 !important',
          bgcolor: 'transparent',
        },
        '& .MuiDataGrid-footerPaginationSelectInputInputBase': {
          borderRadius: '0 !important',
          bgcolor: 'transparent',
        },
        '& .MuiDataGrid-footerPaginationSelectInputInputRoot': {
          borderRadius: '0 !important',
          bgcolor: 'transparent',
        },
        '& .MuiDataGrid-footerPaginationSelectInputInputInput': {
          borderRadius: '0 !important',
          bgcolor: 'transparent',
        },
        '& .MuiDataGrid-footerPaginationSelectInputInputAdornedStart': {
          borderRadius: '0 !important',
          bgcolor: 'transparent',
        },
        '& .MuiDataGrid-footerPaginationSelectInputInputAdornedEnd': {
          borderRadius: '0 !important',
          bgcolor: 'transparent',
        },
        '& .MuiDataGrid-footerPaginationSelectInputInputMultiline': {
          borderRadius: '0 !important',
          bgcolor: 'transparent',
        },
        '& .MuiDataGrid-footerPaginationSelectInputInputHiddenLabel': {
          borderRadius: '0 !important',
          bgcolor: 'transparent',
        },
        '& .MuiDataGrid-footerPaginationSelectInputInputSizeSmall': {
          borderRadius: '0 !important',
          bgcolor: 'transparent',
        },
        '& .MuiDataGrid-footerPaginationSelectInputInputSizeLarge': {
          borderRadius: '0 !important',
          bgcolor: 'transparent',
        },
        '& .MuiDataGrid-footerPaginationSelectInputInputFullWidth': {
          borderRadius: '0 !important',
          bgcolor: 'transparent',
        },
        '& .MuiDataGrid-footerPaginationSelectInputInputDisableUnderline': {
          borderRadius: '0 !important',
          bgcolor: 'transparent',
        },
        '& .MuiDataGrid-footerPaginationSelectInputInputNotchedOutline': {
          borderRadius: '0 !important',
          bgcolor: 'transparent',
        },
        '& .MuiDataGrid-footerPaginationSelectInputInputOutlinedInput': {
          borderRadius: '0 !important',
          bgcolor: 'transparent',
        },
        '& .MuiDataGrid-footerPaginationSelectInputInputFilledInput': {
          borderRadius: '0 !important',
          bgcolor: 'transparent',
        },
        '& .MuiDataGrid-footerPaginationSelectInputInputStandardInput': {
          borderRadius: '0 !important',
          bgcolor: 'transparent',
        }
      }}>
        <DataGrid
          rows={filteredRecords}
          columns={columns(handleView, handleEdit, handleDelete, handleStatusChange, editingStatus, setEditingStatus, handleStatusSave, handleStatusCancel, userSettings)}
          loading={loading}
          pageSize={10}
          rowsPerPageOptions={[10]}
          disableSelectionOnClick
          sx={{
            '& .MuiDataGrid-cell:focus': {
              outline: 'none',
            },
            '& .MuiDataGrid-cell:focus-within': {
              outline: 'none',
            }
          }}
          components={{
            NoRowsOverlay: () => (
              <Box sx={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                color: '#bdbdbd',
                p: 4,
              }}>
                <SecurityIcon sx={{ 
                  fontSize: 64, 
                  mb: 2, 
                  color: '#424242',
                  opacity: 0.7
                }} />
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: '#ffffff',
                    mb: 1,
                    fontWeight: 600
                  }}
                >
                  {searchTerm ? 'No Records Found' : 'No Fraud Records Found'}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    mb: 3,
                    textAlign: 'center',
                    maxWidth: 300
                  }}
                >
                  {searchTerm 
                    ? 'Try adjusting your search terms to find what you\'re looking for.'
                    : 'Get started by creating your first fraud record to track and manage cases.'
                  }
                </Typography>
                {!searchTerm && (
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => navigate('/fraud-records/new')}
                    sx={{
                      borderColor: '#1976d2',
                      color: '#1976d2',
                      '&:hover': {
                        borderColor: '#1565c0',
                        bgcolor: '#1976d210',
                      }
                    }}
                  >
                    Create First Record
                  </Button>
                )}
              </Box>
            ),
          }}
        />
      </Box>
    </Box>
  );
};

export default FraudRecords; 